import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button, Card, Heading, Inline, Stack } from '../atoms';
import { useTranslation } from '../../i18n/useTranslation';
import { PageContent } from '../templates/PageContent/PageContent';
import './ErrorBoundary.css';

/**
 * Logs an uncaught render error. There is no client error-reporting endpoint in the
 * API yet, so the browser console (and whatever collects it) is the sink.
 */
export function reportRenderError(error: unknown, componentStack?: string | null): void {
  console.error('[ReefGuide] Uncaught render error', error, componentStack ?? '');
}

interface ErrorBoundaryProps {
  children: ReactNode;
  /** The boundary clears its error whenever this value changes (e.g. the route path). */
  resetKey?: unknown;
  /** Rendered instead of the crashed subtree. */
  fallback: (reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

/**
 * Catches render/effect errors below it so a crash never unmounts the whole app
 * (which leaves only the dark page background — the livestock crash after #1609).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, hasError: false };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error, hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportRenderError(error, info.componentStack);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = (): void => {
    this.setState({ error: null, hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) return this.props.fallback(this.reset);
    return this.props.children;
  }
}

export interface ErrorFallbackProps {
  /** Navigates to the overview; omitted outside a router (full page load instead). */
  onGoHome?: () => void;
}

/** "Something went wrong" card with reload and back-to-overview actions. */
export function ErrorFallback({ onGoHome }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const goHome = onGoHome ?? (() => window.location.assign('/dashboard'));

  return (
    <PageContent width="narrow" className="rg-error-fallback" data-testid="error-fallback">
      <Card padding="lg">
        <Stack gap="4">
          <Heading level={1}>{t('errorBoundary.title')}</Heading>
          <Alert>{t('errorBoundary.body')}</Alert>
          <Inline gap="3">
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t('errorBoundary.reload')}
            </Button>
            <Button variant="secondary" onClick={goHome}>
              {t('errorBoundary.home')}
            </Button>
          </Inline>
        </Stack>
      </Card>
    </PageContent>
  );
}

/**
 * Error boundary for page content (used by AppLayout around its `main` slot). It
 * depends on no router hooks — AppLayout also renders outside a router — and resets
 * when the browser path changes: navigating to another page remounts the page anyway,
 * and moving within the same page (e.g. another aquarium) re-renders AppLayout with a
 * new path, which clears the error.
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  return (
    <ErrorBoundary resetKey={pathname} fallback={() => <ErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
}
